import { useEffect, useMemo, useRef, useState } from 'react'
import type { RegisterMoteResponse } from '@mote/api'
//FULL PATH CIRCULAR DEPENDENCIES :|
import { getMicrophonePermissionStatus } from '@mote/helpers/hooks/microphone/useMicrophonePermission'
import { moteRecorderErrorMap } from '@mote/helpers/hooks/recorder/errors'
import { useFileContext } from '../../file'
import { useMoteConfigContext } from '../../mote'
import { MicrophoneError } from '../../permissions'
import { SoundPlayer } from '../SoundPlayer'
import {
  ChunkUploadError,
  MoteFinalizationError,
  NotLoggedInRegisterError,
  QuotaExhaustedError,
  QuotaExhaustedRegisterError,
  Recorder,
  RegisterError
} from '../recorder'
import type { NetworkCongestionEvent, RecorderConfig } from '../recorder'
import { usePayloadInjector, useTracker } from '../tracker'

export function useRecorderV4Adapter(
  recorderConfig: Pick<RecorderConfig, 'quotaExhausted'>
) {
  const configContext = useMoteConfigContext()
  const fileContext = useFileContext()
  const tracker = useTracker()
  const payloadInjector = usePayloadInjector()

  const [registeredMote, setMoteStatus] = useState<RegisterMoteResponse | null>(
    null
  )

  const recordingConfigRef = useRef(getRecorderConfig())
  const payloadInjectorRef = useRef(payloadInjector)
  useEffect(() => {
    recordingConfigRef.current = getRecorderConfig()
    payloadInjectorRef.current = payloadInjector
  })

  const [soundPlayer] = useState(() => new SoundPlayer(fileContext.repository))

  const [recorder, setRecorder] = useState(makeRecorder)
  const [status, setStatus] = useState(recorder.status)
  const [error, setError] = useState('')
  const [hasWarning, setHasWarning] = useState(false)
  const [audioContext] = useState(() => new AudioContext())
  const [audioStream, setAudioStream] =
    useState<MediaStreamAudioSourceNode | null>(null)

  const handlers = useMemo(() => {
    return {
      networkCongestion: {
        handleEvent: (event: NetworkCongestionEvent) => {
          tracker.errorOccurred(
            moteRecorderErrorMap.NETWORK_CONGESTION,
            event.detail
          )
          setHasWarning(true)
        }
      },
      async error(_event: Event) {
        const event = _event as ErrorEvent
        const recorder = event.target as Recorder
        setStatus(recorder.status)
        await audioContext.suspend()
        setError(getErrorAndTrack(event.error))
      }
    }
  }, [audioContext, tracker])

  useEffect(() => {
    recorder.addEventListener('error', handlers.error)
    recorder.addEventListener('networkcongestion', handlers.networkCongestion)
    return () => {
      recorder.cancel(false).catch(() => {
        /*NOOP*/
      })
      recorder.removeEventListener('error', handlers.error)
      recorder.removeEventListener(
        'networkcongestion',
        handlers.networkCongestion
      )
    }
  }, [recorder, handlers, audioContext])

  const hasMicrophonePermission = useRef(false)
  useEffect(() => {
    checkForMicrophoneStatus().then(hasPermission => {
      hasMicrophonePermission.current = hasPermission
    })
  }, [])

  const [duration, setDuration] = useState(0)

  async function startRecording() {
    await audioContext.resume()
    try {
      if (!hasMicrophonePermission.current) {
        tracker.microphoneRequestTriggered()
      }
      await recorder.start()
      hasMicrophonePermission.current = true
      if (!hasMicrophonePermission.current) {
        tracker.microphoneRequestEnabled()
      }
      tracker.recordStart()
      recorder.nextTick(setDuration)
      setAudioStream(audioContext.createMediaStreamSource(recorder.stream))
      setStatus(recorder.status)
      recorder.fetchMote().then(setMoteStatus)
    } catch (error) {
      setError(getErrorAndTrack(error))
    }
  }

  async function stopRecording() {
    const stopPromise = recorder.stop()
    setStatus(recorder.status)
    return stopPromise
      .then(([finalizedResponse, blobs]) => {
        tracker.recordFinalized(
          finalizedResponse.moteStatus.duration,
          finalizedResponse.moteStatus.moteId,
          finalizedResponse.moteStatus.transcriptionLanguage
        )
        return [finalizedResponse, blobs] as const
      })
      .catch(e => {
        reset(getErrorAndTrack(e))
        throw e
      })
  }

  async function cancelRecording() {
    recorder.cancel()
    setDuration(0)
    setStatus(recorder.status)
    await audioContext.suspend()
    setMoteStatus(null)
  }

  async function pauseRecording() {
    recorder.pause()
    setStatus(recorder.status)
    await audioContext.suspend()
  }

  async function resumeRecording() {
    await audioContext.resume()
    recorder.resume()
    setStatus(recorder.status)
  }

  const statusObject = {
    raw: status,
    isIdle: status === 'idle',
    isCancelled: status === 'idle',
    isStopped: status === 'stopped',
    isPaused: status === 'paused',
    isRecording: status === 'recording'
  }

  return [
    recorder,
    {
      registeredMote,
      maxSeconds: configContext.maxSeconds,
      seconds: configContext.maxSeconds - duration,
      audioContext,
      audioStream,
      status: statusObject,
      startRecording,
      stopRecording,
      cancelRecording,
      pauseRecording,
      resumeRecording,
      error,
      reset,
      hasWarning
    }
  ] as const

  function makeRecorder() {
    return new Recorder(soundPlayer, payloadInjectorRef, recordingConfigRef)
  }

  function reset(error = '') {
    const recorder = makeRecorder()
    setError(error)
    setRecorder(recorder)
    setStatus(recorder.status)
    setHasWarning(false)
    setDuration(0)
  }

  function getErrorAndTrack(error: unknown) {
    let payload = {}
    function getMoteRecorderError(error: unknown) {
      switch (true) {
        case error instanceof MicrophoneError:
          tracker.microphoneRequestBlocked()
          return moteRecorderErrorMap.FIX_MICROPHONE_PERMISSION
        case error instanceof QuotaExhaustedError:
        case error instanceof QuotaExhaustedRegisterError:
          tracker.quotaExhausted()
          return moteRecorderErrorMap.QUOTA_EXCEEDED
        case error instanceof NotLoggedInRegisterError:
          return moteRecorderErrorMap.NOT_LOGGED_IN
        case error instanceof ChunkUploadError:
          let e = error as ChunkUploadError
          payload = e.toJSON()
          return moteRecorderErrorMap.CHUNK_UPLOAD_ERROR
        case error instanceof MoteFinalizationError:
          payload = { errorCode: (error as MoteFinalizationError).errorCode }
          return moteRecorderErrorMap.NETWORK_ERROR
        case error instanceof RegisterError:
          return moteRecorderErrorMap.NETWORK_ERROR
        default:
          return moteRecorderErrorMap.UNKNOWN_ERROR
      }
    }
    const moteError = getMoteRecorderError(error)
    tracker.errorOccurred(moteError, payload)
    return moteError
  }

  function getRecorderConfig() {
    return {
      quotaExhausted: recorderConfig.quotaExhausted,
      millisecondsPerChunk: configContext.millisecondsPerChunk,
      maxRecordingTimeSeconds: configContext.maxSeconds
    }
  }

  async function checkForMicrophoneStatus() {
    try {
      const permissionStatus = await getMicrophonePermissionStatus()
      return permissionStatus.state === 'granted'
    } catch {
      return false
    }
  }
}
